import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiEdit2, FiFileText, FiPaperclip, FiPlus, FiRotateCcw, FiSettings, FiShoppingBag, FiUploadCloud } from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { FadeIn, TabFade } from '../components/motion';
import MoreMenu from '../components/MoreMenu';
import Modal from '../components/Modal';
import LoadingButton from '../components/LoadingButton';
import {
  EmptyState as ErpEmpty,
  ListState as ErpListState,
  StatusBadge,
  formatCents as money,
} from '../components/shared/Ui';
import './Services.css';
import '../components/erp/Erp.css';
import './Requisitions.css';
import { appAlert, appConfirm } from '../utils/appDialog';
import { queryKeys, staleTimes } from '../config/queryKeys';
import { companyOptionLabel } from '../utils/legalCompany';
import ListPager from '../components/ListPager';
import { useClientPager } from '../hooks/useClientPager';

const TABS = [
  { id: 'pos', label: 'Vendor POs', icon: FiShoppingBag },
  { id: 'quotations', label: 'Quotations', icon: FiFileText },
  { id: 'bills', label: 'Bills', icon: FiFileText },
];

const BUYING_EXTERNAL_STAGE_KEYS = new Set(['vendor_approval', 'external_approval']);

function buyingStageKeys(stages, flag, fallback) {
  const keys = (stages || []).filter((s) => s[flag]).map((s) => s.stage_key);
  return keys.length ? keys : fallback;
}

const Buying = () => {
  const { hasPermission } = usePermissions();
  const canWorkflow = hasPermission('erp_buying_workflow_manage');
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [assigneeDraft, setAssigneeDraft] = useState({});
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const [tab, setTab] = useState(TABS.some((t) => t.id === requested) ? requested : 'pos');
  const [companyFilter, setCompanyFilter] = useState('');

  useEffect(() => {
    if (TABS.some((t) => t.id === requested)) setTab(requested);
  }, [requested]);

  const companies = useQuery(
    queryKeys.accountingCompanies,
    async () => (await api.get('/accounting/companies')).data,
    { staleTime: staleTimes.reference }
  );

  const buyingParams = {
    limit: 500,
    ...(companyFilter ? { company_id: companyFilter } : {}),
  };
  const pos = useQuery(
    queryKeys.vendorPos({ companyId: companyFilter || 'all' }),
    async () => (await api.get('/buying/purchase-orders', { params: buyingParams })).data,
    { enabled: tab === 'pos' }
  );
  const quotations = useQuery(
    queryKeys.vendorQuotations({ companyId: companyFilter || 'all' }),
    async () => (await api.get('/buying/quotations', { params: buyingParams })).data,
    { enabled: tab === 'quotations' }
  );
  const bills = useQuery(
    queryKeys.vendorBills({ companyId: companyFilter || 'all' }),
    async () => (await api.get('/buying/bills', { params: buyingParams })).data,
    { enabled: tab === 'bills' }
  );

  const postBill = useMutation(
    async (id) => (await api.post(`/buying/bills/${id}/post`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries('erp-vbills');
        qc.invalidateQueries(['buying', 'vendor-bills']);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );
  const unpostBill = useMutation(
    async (id) => (await api.post(`/buying/bills/${id}/unpost`)).data,
    {
      onSuccess: () => {
        qc.invalidateQueries('erp-vbills');
        qc.invalidateQueries(['buying', 'vendor-bills']);
        qc.invalidateQueries(queryKeys.accountingJournals);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const poRows = pos.data || [];
  const quoteRows = quotations.data || [];
  const billRows = bills.data || [];
  const {
    pageItems: pagedPoRows,
    pagination: poPagination,
    setPage: setPoPage,
  } = useClientPager(poRows, { resetKey: companyFilter });
  const {
    pageItems: pagedQuoteRows,
    pagination: quotePagination,
    setPage: setQuotePage,
  } = useClientPager(quoteRows, { resetKey: companyFilter });
  const {
    pageItems: pagedBillRows,
    pagination: billPagination,
    setPage: setBillPage,
  } = useClientPager(billRows, { resetKey: companyFilter });
  const companyRows = companies.data || [];

  const { data: workflow } = useQuery(
    queryKeys.buyingWorkflow,
    () => api.get('/buying/workflow').then((r) => r.data),
    { staleTime: staleTimes.reference }
  );
  const { data: users = [] } = useQuery(
    queryKeys.usersPicker('buying-assignees'),
    () =>
      api
        .get('/users')
        .then((r) => (r.data || []).filter((u) => u.is_active && u.role !== 'candidate')),
    { enabled: showWorkflow && canWorkflow, staleTime: staleTimes.lists }
  );
  const workflowStages = useMemo(() => workflow?.stages || [], [workflow?.stages]);
  const editableStatusKeys = useMemo(() => {
    const initial = buyingStageKeys(workflowStages, 'is_initial', ['draft']);
    const rejected = buyingStageKeys(workflowStages, 'is_rejected', ['rejected']);
    return new Set([...initial, ...rejected]);
  }, [workflowStages]);
  const completedStatusKeys = useMemo(() => {
    const keys = buyingStageKeys(workflowStages, 'is_completed', ['approved']);
    return new Set([...keys, 'approved']);
  }, [workflowStages]);
  useEffect(() => {
    if (!showWorkflow) return;
    const draft = {};
    for (const s of workflowStages) {
      draft[s.stage_key] = (s.assignees || []).map((a) => String(a.user_id));
    }
    setAssigneeDraft(draft);
  }, [showWorkflow, workflowStages]);

  const saveAssigneesMutation = useMutation(
    ({ stage_key, user_ids }) => api.put('/buying/workflow/assignees', { stage_key, user_ids }),
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.buyingWorkflow);
        void appAlert('Workflow assignees saved.');
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const companySelect = (
    <div className="erp-books-picker">
      <label htmlFor="buying-company">Company</label>
      <select
        id="buying-company"
        value={companyFilter}
        onChange={(e) => setCompanyFilter(e.target.value)}
        disabled={companies.isLoading}
      >
        <option value="">All companies</option>
        {companyRows.map((c) => (
          <option key={c.id} value={c.id}>
            {companyOptionLabel(c)}
          </option>
        ))}
      </select>
    </div>
  );

  const changeTab = (id) => {
    setTab(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="erp-page list-page">
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1><FiShoppingBag aria-hidden /> Buying</h1>
          <p className="page-subtitle">
            Vendor quotations, POs, and supplier bills — attach the vendor copy to R2. When OCR is
            on, the file is scanned on upload and AI fills the form fields. Documents stay on their
            legal company even if the default company changes. Draft → Internal review → Internal
            approval → Vendor approval → Approved. Post bills to the ledger only after they are
            approved.
          </p>
        </div>
        {canWorkflow && (
          <button type="button" className="btn btn-secondary" onClick={() => setShowWorkflow(true)}>
            <FiSettings /> Workflow
          </button>
        )}
      </FadeIn>

      <div className="erp-toolbar-row">
        <div className="erp-tabs" role="tablist" aria-label="Buying sections">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`erp-tab ${tab === id ? 'is-active' : ''}`}
              onClick={() => changeTab(id)}
            >
              <Icon aria-hidden />
              {label}
            </button>
          ))}
        </div>
        {companySelect}
      </div>

      <TabFade tabKey={tab}>
        {tab === 'pos' && (
          <section className="erp-panel">
            <div className="erp-panel-head">
              <div>
                <h2>Vendor POs</h2>
                <p className="erp-panel-meta">
                  {poRows.length} {poRows.length === 1 ? 'Vendor PO' : 'Vendor POs'}
                </p>
              </div>
              {hasPermission('erp_vendor_po_manage') && (
                <Link className="btn btn-primary btn-sm" to="/buying/purchase-orders/new">
                  <FiPlus aria-hidden /> New Vendor PO
                </Link>
              )}
            </div>
            <ErpListState
              query={pos}
              isEmpty={poRows.length === 0}
              empty={
                <ErpEmpty
                  icon={FiShoppingBag}
                  title="No Vendor POs yet"
                  hint="Raise a Vendor PO against a supplier to track committed spend before the bill arrives."
                />
              }
            >
              <div className="erp-table-scroll">
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th scope="col">Number</th>
                      <th scope="col">Vendor</th>
                      <th scope="col">Company</th>
                      <th scope="col">Date</th>
                      <th scope="col">Currency</th>
                      <th scope="col" className="erp-num">Total</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedPoRows.map((p) => (
                      <tr key={p.id}>
                        <td className="erp-cell-primary">
                          <Link to={`/buying/purchase-orders/${p.id}`}>
                            <span className="erp-code">{p.po_number}</span>
                          </Link>
                        </td>
                        <td>{p.vendor_name || <span className="erp-code">{p.vendor_id}</span>}</td>
                        <td>{p.company_name || p.company_country || <span className="erp-muted">—</span>}</td>
                        <td>{p.order_date}</td>
                        <td><span className="erp-code">{p.currency || 'USD'}</span></td>
                        <td className="erp-num">{money(p.total_cents, p.currency)}</td>
                        <td><StatusBadge status={p.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPager
                page={poPagination.page}
                pageCount={poPagination.pageCount}
                total={poPagination.total}
                limit={poPagination.limit}
                onPageChange={setPoPage}
              />
            </ErpListState>
          </section>
        )}

        {tab === 'quotations' && (
          <section className="erp-panel">
            <div className="erp-panel-head">
              <div>
                <h2>Vendor quotations</h2>
                <p className="erp-panel-meta">
                  {quoteRows.length} {quoteRows.length === 1 ? 'quotation' : 'quotations'}
                </p>
              </div>
              {hasPermission('erp_vendor_quote_manage') && (
                <Link className="btn btn-primary btn-sm" to="/buying/quotations/new">
                  <FiPlus aria-hidden /> New quotation
                </Link>
              )}
            </div>
            <ErpListState
              query={quotations}
              isEmpty={quoteRows.length === 0}
              empty={
                <ErpEmpty
                  icon={FiFileText}
                  title="No vendor quotations yet"
                  hint="Upload a supplier quotation copy and enter the quoted amounts."
                />
              }
            >
              <div className="erp-table-scroll">
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th scope="col">Number</th>
                      <th scope="col">Vendor</th>
                      <th scope="col">Vendor PO</th>
                      <th scope="col">Company</th>
                      <th scope="col">Date</th>
                      <th scope="col">Currency</th>
                      <th scope="col" className="erp-num">Total</th>
                      <th scope="col">Copy</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedQuoteRows.map((q) => (
                      <tr key={q.id}>
                        <td className="erp-cell-primary">
                          <Link to={`/buying/quotations/${q.id}`}>
                            <span className="erp-code">{q.quotation_number}</span>
                          </Link>
                        </td>
                        <td>{q.vendor_name || <span className="erp-code">{q.vendor_id}</span>}</td>
                        <td>
                          {q.vendor_po_id ? (
                            <Link to={`/buying/purchase-orders/${q.vendor_po_id}`}>
                              <span className="erp-code">{q.vendor_po_number || q.vendor_po_id}</span>
                            </Link>
                          ) : (
                            <span className="erp-muted">—</span>
                          )}
                        </td>
                        <td>{q.company_name || q.company_country || <span className="erp-muted">—</span>}</td>
                        <td>{q.quotation_date}</td>
                        <td><span className="erp-code">{q.currency || 'USD'}</span></td>
                        <td className="erp-num">{money(q.total_cents, q.currency)}</td>
                        <td>
                          {q.source_file_r2_key ? (
                            <FiPaperclip aria-label="Source copy attached" title="Source copy attached" />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td><StatusBadge status={q.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPager
                page={quotePagination.page}
                pageCount={quotePagination.pageCount}
                total={quotePagination.total}
                limit={quotePagination.limit}
                onPageChange={setQuotePage}
              />
            </ErpListState>
          </section>
        )}

        {tab === 'bills' && (
          <section className="erp-panel">
            <div className="erp-panel-head">
              <div>
                <h2>Bills</h2>
                <p className="erp-panel-meta">
                  {billRows.length} {billRows.length === 1 ? 'bill' : 'bills'}
                </p>
              </div>
              {hasPermission('erp_bills_manage') && (
                <Link className="btn btn-primary btn-sm" to="/buying/bills/new">
                  <FiPlus aria-hidden /> New bill
                </Link>
              )}
            </div>
            <ErpListState
              query={bills}
              isEmpty={billRows.length === 0}
              empty={
                <ErpEmpty
                  icon={FiFileText}
                  title="No bills yet"
                  hint="Enter a vendor bill, attach the supplier copy, then post to write the GL entry."
                />
              }
            >
              <div className="erp-table-scroll">
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th scope="col">Number</th>
                      <th scope="col">Vendor</th>
                      <th scope="col">Vendor PO</th>
                      <th scope="col">Company</th>
                      <th scope="col">Date</th>
                      <th scope="col">Currency</th>
                      <th scope="col" className="erp-num">Total</th>
                      <th scope="col">Copy</th>
                      <th scope="col">Status</th>
                      <th scope="col" className="erp-cell-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedBillRows.map((b) => (
                      <tr key={b.id}>
                        <td className="erp-cell-primary">
                          <Link to={`/buying/bills/${b.id}`}>
                            <span className="erp-code">{b.bill_number}</span>
                          </Link>
                        </td>
                        <td>{b.vendor_name || <span className="erp-code">{b.vendor_id}</span>}</td>
                        <td>
                          {b.vendor_po_id ? (
                            <Link to={`/buying/purchase-orders/${b.vendor_po_id}`}>
                              <span className="erp-code">{b.vendor_po_number || b.vendor_po_id}</span>
                            </Link>
                          ) : (
                            <span className="erp-muted">—</span>
                          )}
                        </td>
                        <td>{b.company_name || b.company_country || <span className="erp-muted">—</span>}</td>
                        <td>{b.bill_date}</td>
                        <td><span className="erp-code">{b.currency || 'USD'}</span></td>
                        <td className="erp-num">{money(b.total_cents, b.currency)}</td>
                        <td>
                          {b.source_file_r2_key ? (
                            <FiPaperclip aria-label="Source copy attached" title="Source copy attached" />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td><StatusBadge status={b.status} /></td>
                        <td className="erp-cell-actions">
                          <MoreMenu
                            label={`Actions for ${b.bill_number}`}
                            size="sm"
                            items={[
                              {
                                id: 'edit',
                                label: 'Edit',
                                icon: FiEdit2,
                                hidden:
                                  !editableStatusKeys.has(b.status) ||
                                  !hasPermission('erp_bills_manage'),
                                onSelect: () => navigate(`/buying/bills/${b.id}`),
                              },
                              {
                                id: 'post-gl',
                                label: postBill.isLoading ? 'Posting…' : 'Post to GL',
                                icon: FiUploadCloud,
                                hidden:
                                  !completedStatusKeys.has(b.status) ||
                                  !hasPermission('erp_journals_post'),
                                disabled: postBill.isLoading,
                                onSelect: () => postBill.mutate(b.id),
                              },
                              {
                                id: 'unpost-gl',
                                label: unpostBill.isLoading ? 'Unposting…' : b.approved_at ? 'Unpost' : 'Revert to draft',
                                icon: FiRotateCcw,
                                hidden:
                                  b.status !== 'posted' ||
                                  Number(b.amount_paid_cents) > 0 ||
                                  !hasPermission('erp_journals_post'),
                                disabled: unpostBill.isLoading,
                                onSelect: async () => {
                                  const ok = await appConfirm(
                                    b.approved_at
                                      ? `Unpost ${b.bill_number}? Its GL entry will leave the ledger and the bill returns to Approved.`
                                      : `Revert ${b.bill_number} to draft? Its GL entry will leave the ledger so you can correct the bill and post again.`
                                  );
                                  if (ok) unpostBill.mutate(b.id);
                                },
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPager
                page={billPagination.page}
                pageCount={billPagination.pageCount}
                total={billPagination.total}
                limit={billPagination.limit}
                onPageChange={setBillPage}
              />
            </ErpListState>
          </section>
        )}
      </TabFade>

      <Modal open={showWorkflow} onClose={() => setShowWorkflow(false)} ariaLabel="Buying workflow">
        <h2>Approval workflow</h2>
        <p className="form-hint">
          Draft → Internal review → Internal approval → Vendor approval → Approved. Optional
          company-wide assignees per stage for vendor POs, quotations, and bills. When assignees
          are set, each notify stage uses an AND gate — every assignee must approve before the
          stage advances. Each vendor’s Internal contacts (Reviewer / Approver) and Vendor contacts
          (Vendor approver) are included automatically at the matching stage. When a document
          enters a notify stage, assignees receive email with Approve / Reject links (if Email
          actions is enabled on the status).
        </p>
        {(workflow?.stages || []).map((stage) => (
          <div key={stage.stage_key} className="req-workflow-stage">
            <h3>
              {stage.label}
              {!stage.notify_assignees ? ' (no notifications)' : ''}
            </h3>
            <p className="svc-panel-meta">{stage.description}</p>
            {stage.notify_assignees ? (
              <>
                <select
                  multiple
                  size={Math.min(
                    6,
                    Math.max(
                      3,
                      (BUYING_EXTERNAL_STAGE_KEYS.has(stage.stage_key)
                        ? users
                        : users.filter((u) => u.role !== 'external')
                      ).length || 3
                    )
                  )}
                  value={assigneeDraft[stage.stage_key] || []}
                  onChange={(e) => {
                    const selected = [...e.target.selectedOptions].map((o) => o.value);
                    setAssigneeDraft((prev) => ({ ...prev, [stage.stage_key]: selected }));
                  }}
                >
                  {(BUYING_EXTERNAL_STAGE_KEYS.has(stage.stage_key)
                    ? users
                    : users.filter((u) => u.role !== 'external')
                  ).map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {u.first_name} {u.last_name} ({u.role})
                    </option>
                  ))}
                </select>
                <LoadingButton
                  type="button"
                  className="btn btn-secondary btn-sm"
                  loading={saveAssigneesMutation.isLoading}
                  onClick={() =>
                    saveAssigneesMutation.mutate({
                      stage_key: stage.stage_key,
                      user_ids: (assigneeDraft[stage.stage_key] || []).map(Number),
                    })
                  }
                >
                  Save {stage.label} assignees
                </LoadingButton>
              </>
            ) : (
              <p className="svc-panel-meta">Authors work in draft; no assignee notifications.</p>
            )}
          </div>
        ))}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setShowWorkflow(false)}>
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Buying;
